import { CspError, ErrorInfo, Maybe } from '@csp/csp-common-model';
import { DrawerToggle, useDrawerToggle } from '@csp/csp-web-hooks';
import { toRestOptions } from '@csp/csp-fe-auth';
import { useAbortController } from '@csp/fe-hooks';
import { StudyDocsUtil } from '../../util/StudyDocsUtil';
import { useMutation } from '../../../../common/query/hooks/useMutation';
import { StaticContentService } from '../../../../common/service/StaticContentService';
import { DownloadFileParams } from '../model/DownloadFileParams';
import { UseMutationState } from '../../../../common/query/models/UseMutationState';
import { useErrorInfo } from '../../../../common/hooks/useErrorInfo';

type Out = {
  isPending: boolean;
  downloadFile: UseMutationState<void, DownloadFileParams>['mutate'];
  cancelFileDownload: () => Promise<void>;
  error: Maybe<ErrorInfo>;
  dialogToggle: DrawerToggle;
};

export const useDownloadFile = (): Out => {
  const dialogToggle = useDrawerToggle();
  const { alertError } = StaticContentService.getGenericBlock('webHcpDocumentRepository', 'documents');
  const { abort, signal } = useAbortController();
  const { errorInfo, setErrorInfo, clearErrorInfo } = useErrorInfo();

  const cancelFileDownload = async (): Promise<void> => {
    abort('Cancelled by user');
    await dialogToggle.closeAsync();
  };

  const { mutate, isPending } = useMutation<void, DownloadFileParams>(
    async ({ fileName, fileId }: DownloadFileParams) => {
      await StudyDocsUtil.downloadFile(fileName, fileId, toRestOptions({ signal }));
      await dialogToggle.closeAsync();
    },
    {
      onMutate: () => {
        clearErrorInfo();
        dialogToggle.open();
      },
      onError: (error: unknown) => {
        if (CspError.isCancelled(error)) {
          return;
        }

        setErrorInfo({
          message: alertError.errorDownloadingFile.text,
          error,
          handled: true,
        });
      },
    },
  );

  return {
    downloadFile: mutate,
    cancelFileDownload,
    isPending,
    error: errorInfo,
    dialogToggle,
  };
};
